import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Receipt, Search, Undo2 } from 'lucide-react';
import { formatShortDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { money } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Stat,
  Table,
  Td,
  Textarea,
  Th,
  type PageMeta,
} from '@/components/ui';
import { DeclarationQueue } from '@/components/DeclarationQueue';
import { StatusBadge } from '@/components/StatusBadge';

const REPAYMENT_STATUS = {
  PENDING: { label: 'Pending', tone: 'warning' as const },
  CONFIRMED: { label: 'Confirmed', tone: 'success' as const },
  REVERSED: { label: 'Reversed', tone: 'danger' as const },
  FAILED: { label: 'Failed', tone: 'danger' as const },
};

interface RepaymentRow {
  id: string;
  reference: string;
  loanId: string;
  loanNumber: string;
  memberName: string;
  membershipNumber: string;
  amount: string;
  paymentDate: string;
  paymentMethod: string;
  transactionReference: string | null;
  status: string;
  recordedBy: string;
  reversedAt: string | null;
  reversalReason: string | null;
}

export default function AdminRepayments() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [reversing, setReversing] = useState<RepaymentRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['repayments', page, query, status],
    queryFn: () =>
      api.get<{
        data: RepaymentRow[];
        totalConfirmedFormatted: string;
        meta: PageMeta;
      }>(
        `/repayments?pageSize=25&page=${page}${query ? `&q=${encodeURIComponent(query)}` : ''}${
          status ? `&status=${status}` : ''
        }`,
      ),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="Repayments"
        description="Every payment received, for reconciliation."
      />

      <DeclarationQueue />

      {data && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat
            label="Total collected"
            value={data.totalConfirmedFormatted}
            hint="Confirmed payments only — reversals excluded"
            tone="success"
            icon={<Receipt size={16} />}
          />
          <Stat label="Payments recorded" value={data.meta.total} />
          <Stat
            label="Reversed"
            value={data.data.filter((r) => r.status === 'REVERSED').length}
            tone={data.data.some((r) => r.status === 'REVERSED') ? 'warning' : 'neutral'}
            hint="On this page"
          />
        </div>
      )}

      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <Field label="Search" htmlFor="q" className="min-w-[240px] flex-1">
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-secondary"
              />
              <Input
                id="q"
                className="pl-9"
                placeholder="Reference, loan number, bank reference or member"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </div>
          </Field>

          <Field label="Status" htmlFor="status" className="w-48">
            <Select id="status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {Object.entries(REPAYMENT_STATUS).map(([key, presentation]) => (
                <option key={key} value={key}>
                  {presentation.label}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          icon={<Receipt size={20} />}
          title="No payments recorded"
          description="Payments are recorded from a loan's detail page."
        />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Reference</Th>
                  <Th>Loan</Th>
                  <Th>Member</Th>
                  <Th numeric>Amount</Th>
                  <Th>Method</Th>
                  <Th>Bank reference</Th>
                  <Th>Date</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.data.map((repayment) => (
                  <tr
                    key={repayment.id}
                    className={`transition-colors hover:bg-muted/50 ${
                      repayment.status === 'REVERSED' ? 'opacity-60' : ''
                    }`}
                  >
                    <Td className="numeric font-semibold">{repayment.reference}</Td>
                    <Td>
                      <Link
                        to={`/admin/loans/${repayment.loanId}`}
                        className="numeric font-semibold text-primary hover:underline"
                      >
                        {repayment.loanNumber}
                      </Link>
                    </Td>
                    <Td>
                      <p className="font-medium text-foreground">{repayment.memberName}</p>
                      <p className="numeric text-xs text-foreground-secondary">
                        {repayment.membershipNumber}
                      </p>
                    </Td>
                    <Td
                      numeric
                      className={
                        repayment.status === 'REVERSED' ? 'line-through' : 'font-semibold'
                      }
                    >
                      {money(repayment.amount)}
                    </Td>
                    <Td className="text-foreground-secondary">
                      {repayment.paymentMethod.replace(/_/g, ' ').toLowerCase()}
                    </Td>
                    <Td className="numeric text-xs text-foreground-secondary">
                      {repayment.transactionReference ?? '—'}
                    </Td>
                    <Td className="numeric text-foreground-secondary">
                      {formatShortDate(new Date(repayment.paymentDate))}
                    </Td>
                    <Td>
                      <StatusBadge map={REPAYMENT_STATUS} status={repayment.status} />
                    </Td>
                    <Td>
                      {repayment.status === 'CONFIRMED' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReversing(repayment)}
                          aria-label="Reverse"
                        >
                          <Undo2 size={14} />
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </CardContent>
        </Card>
      )}

      {reversing && (
        <ReversePanel repayment={reversing} onClose={() => setReversing(null)} />
      )}
    </div>
  );
}

function ReversePanel({
  repayment,
  onClose,
}: {
  repayment: RepaymentRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const reverse = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/repayments/reverse', {
        repaymentId: repayment.id,
        reason,
      }),
    onSuccess: (result) => {
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ['repayments'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/50 p-0 sm:items-center sm:p-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Reverse {repayment.reference}</CardTitle>
          <p className="text-sm text-foreground-secondary">
            {money(repayment.amount)} on {repayment.loanNumber} — {repayment.memberName}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert tone="warning" title="This restores the balance">
            The loan's outstanding balance goes back up by {money(repayment.amount)}, and the
            affected instalments return to unpaid. If the loan was completed, it reopens and its
            guarantors are reinstated.
          </Alert>

          <Field
            label="Reason for reversal"
            htmlFor="reason"
            required
            hint="Recorded permanently against this payment."
          >
            <Textarea
              id="reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Bank transfer bounced; recorded against the wrong loan."
            />
          </Field>

          <p className="text-xs text-foreground-secondary">
            The payment is never deleted — it stays on the ledger marked REVERSED, with your name
            and this reason.
          </p>

          <div className="flex gap-3 border-t border-border pt-4">
            <Button
              variant="danger"
              full
              loading={reverse.isPending}
              disabled={reason.trim().length < 5}
              onClick={() => reverse.mutate()}
            >
              <Undo2 size={16} /> Reverse payment
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
